import { useEffect, useRef, useState, type CSSProperties } from "react";
import { pickText } from "@heroncendre/player";
import { useOscillationCamera } from "./useOscillationCamera.js";
import { useRotationVisiteur } from "./useRotationVisiteur.js";
import { COULEURS, POLICE_TITRE_SPLASH, POLICE_UI_SPLASH } from "./splashStyles.js";
import textes from "./indicateurRotation.json";
import "./lookAroundHint.css";

/**
 * ============================================================================
 *  LookAroundHint.tsx — « Regardez autour de vous »
 * ----------------------------------------------------------------------------
 *  Montre au visiteur qu'il peut faire tourner la vue, et s'efface dès qu'il le
 *  fait : il doit comprendre qu'il est acteur de la visite. Référence de
 *  design : `sources/design/design_handoff_indicateur_rotation/` (thème Écrin,
 *  mêmes couleurs et fontes que l'accueil).
 *
 *  ── Le geste traverse l'indicateur ─────────────────────────────────────────
 *
 *  La couche entière est `pointer-events: none` : le glisser doit atteindre le
 *  canvas, et c'est le moteur qui tourne la vue. Seul « J'ai compris » capte
 *  les clics. L'indicateur ne gère donc AUCUN geste : il observe la rotation
 *  réelle (`useRotationVisiteur`) et, au repos, fait osciller la vraie caméra
 *  en phase avec la main (`useOscillationCamera`).
 *
 *  ── Ce qu'il ne décide pas ─────────────────────────────────────────────────
 *
 *  QUAND il s'affiche et s'il a déjà été vu : c'est `IndicateurRotation`. Ici,
 *  une seule vie : il apparaît, il se ferme, il prévient (`onDismiss`), en
 *  disant si le visiteur a coché « Ne plus afficher ». La case ne ferme rien
 *  d'elle-même : elle qualifie la fermeture, quelle qu'elle soit (bouton ou
 *  vue tournée).
 * ==========================================================================*/

const SAUGE = COULEURS.sauge;
const CREME = COULEURS.creme;
const TEXTE_SOUS_TITRE = "#EDE4D5";

const CYCLE_MS = 3200;
const CYCLE = `${CYCLE_MS / 1000}s ease-in-out infinite`;
/** Apparition après l'arrivée sur la scène ; plus tôt sans animation. */
const DELAI_MS = 900;
const DELAI_REDUIT_MS = 200;
/** Cap parcouru par le visiteur au-delà duquel il a compris (≈140 px du prototype). */
const SEUIL_DEG = 20;
/** Oscillation de la caméra : ±1,6 % de la largeur dans le prototype. */
const OSCILLATION_DEG = 4;
const PASTILLE_MS = 2800;

export interface LookAroundHintProps {
  lang: string;
  device: "touch" | "mouse";
  /** `nePlusAfficher` : la case était cochée au moment de la fermeture. */
  onDismiss: (nePlusAfficher: boolean) => void;
  reducedMotion?: boolean;
  /**
   * Les flèches tournent-elles la vue ici ? En parcours narratif elles
   * appartiennent à la voix off : le sous-titre qui les promet est alors tu.
   */
  clavier?: boolean;
  /**
   * La caméra peut-elle osciller ? Pas pendant la voix off : les plans la
   * conduisent déjà, une seconde écriture du cap s'y battrait.
   */
  oscillation?: boolean;
  /** Hauteur réservée en bas de l'écran (bandeau, commandes) : la pastille se pose au-dessus. */
  basReserve?: number;
}

export function LookAroundHint({
  lang,
  device,
  onDismiss,
  reducedMotion = false,
  clavier = true,
  oscillation = true,
  basReserve = 0,
}: LookAroundHintProps) {
  const [montre, setMontre] = useState(false);
  const [bouge, setBouge] = useState(false);
  const [ferme, setFerme] = useState(false);
  const [pastille, setPastille] = useState(false);
  /** La pastille a été demandée : son texte reste monté pendant qu'elle s'efface. */
  const [felicite, setFelicite] = useState(false);
  const montreRef = useRef(false);
  montreRef.current = montre;
  const fermeRef = useRef(false);
  const [nePlusAfficher, setNePlusAfficher] = useState(false);
  // Lue au moment de la fermeture, qui peut venir d'une rotation (rappel
  // installé avant que la case ne change).
  const nePlusAfficherRef = useRef(false);
  nePlusAfficherRef.current = nePlusAfficher;
  const minuteurs = useRef<number[]>([]);

  useEffect(() => {
    const liste = minuteurs.current;
    liste.push(window.setTimeout(() => setMontre(true), reducedMotion ? DELAI_REDUIT_MS : DELAI_MS));
    return () => liste.forEach(window.clearTimeout);
    // L'entrée se joue une fois, au montage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fermer = (avecPastille: boolean) => {
    if (fermeRef.current) return;
    fermeRef.current = true;
    setFerme(true);
    setPastille(avecPastille);
    setFelicite(avecPastille);
    onDismiss(nePlusAfficherRef.current);
    if (avecPastille) minuteurs.current.push(window.setTimeout(() => setPastille(false), PASTILLE_MS));
  };

  useRotationVisiteur({
    actif: !ferme,
    clavier,
    seuilDeg: SEUIL_DEG,
    onGeste: () => setBouge(true),
    // Tourné AVANT l'apparition : il sait déjà faire, inutile de le féliciter.
    onSeuil: () => fermer(montreRef.current),
  });

  const visible = montre && !ferme;
  // Le premier mouvement arrête tout : rien n'oscille sous la main du visiteur.
  const anime = visible && !bouge && !reducedMotion;
  useOscillationCamera(anime && oscillation, OSCILLATION_DEG, CYCLE_MS);

  const t = (texte: Record<string, string>) => pickText(texte, lang) ?? "";
  const libelles = device === "touch" ? textes.doigt : textes.souris;
  const sousTitre = device === "touch" || clavier ? t(libelles.sousTitre) : null;

  return (
    <div style={racine}>
      <div aria-hidden style={{ ...voile, opacity: visible ? 1 : 0 }} />

      <div
        aria-live="polite"
        style={{
          ...colonne,
          opacity: visible ? 1 : 0,
          transform: visible || reducedMotion ? "none" : ferme ? "scale(.97)" : "translateY(10px)",
        }}
      >
        {/* Monté dès l'apparition et jusqu'au bout : le fondu de sortie a besoin du contenu. */}
        {montre && (
          <>
            <Pictogramme device={device} anime={anime} />
            <div style={textesStyle}>
              <div style={surtitre}>
                <Losange />
                <span>{t(textes.surtitre)}</span>
                <Losange />
              </div>
              <h2 style={titre}>{t(libelles.titre)}</h2>
              {sousTitre && <p style={sousTitreStyle}>{sousTitre}</p>}
            </div>
            <button
              type="button"
              className="ir-compris"
              style={{ ...bouton, pointerEvents: visible ? "auto" : "none" }}
              tabIndex={visible ? 0 : -1}
              onClick={(event) => {
                fermer(false);
                // Le bouton disparaît : le focus ne doit pas rester sur un fantôme.
                event.currentTarget.blur();
              }}
            >
              {t(textes.compris)}
            </button>
            <label style={{ ...caseLibelle, pointerEvents: visible ? "auto" : "none" }}>
              <input
                type="checkbox"
                checked={nePlusAfficher}
                tabIndex={visible ? 0 : -1}
                onChange={(event) => setNePlusAfficher(event.target.checked)}
                style={caseStyle}
              />
              {t(textes.nePlusAfficher)}
            </label>
          </>
        )}
      </div>

      <div
        aria-live="polite"
        style={{
          ...pastilleCadre,
          bottom: `calc(${basReserve}px + clamp(28px, 8cqh, 72px))`,
          opacity: pastille ? 1 : 0,
          transform: pastille || reducedMotion ? "none" : "translateY(8px)",
        }}
      >
        {felicite && (
          <div style={pastilleStyle}>
            <Losange />
            {t(textes.pastille)}
          </div>
        )}
      </div>
    </div>
  );
}

/* --- le pictogramme ------------------------------------------------------- */

function Pictogramme({ device, anime }: { device: "touch" | "mouse"; anime: boolean }) {
  return (
    <div aria-hidden style={{ position: "relative", width: 230, height: 96, display: "flex", justifyContent: "center" }}>
      <svg
        width={230}
        height={54}
        viewBox="0 0 230 54"
        fill="none"
        style={{ position: "absolute", left: "50%", bottom: 0, transform: "translateX(-50%)" }}
      >
        <path
          d="M28 20 C 10 26, 10 36, 40 42 C 80 50, 150 50, 190 42 C 220 36, 220 26, 202 20"
          stroke={CREME}
          strokeWidth={1.2}
          strokeOpacity={0.7}
          strokeDasharray="2 5"
          strokeLinecap="round"
        />
        <path
          d="M22 14 L28 20 L20 23"
          stroke={SAUGE}
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ animation: anime ? `ir-trail-l ${CYCLE}` : "none" }}
        />
        <path
          d="M208 14 L202 20 L210 23"
          stroke={SAUGE}
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ animation: anime ? `ir-trail-r ${CYCLE}` : "none" }}
        />
      </svg>
      <div
        style={{
          position: "relative",
          animation: anime ? `ir-swipe ${CYCLE}` : "none",
          filter: "drop-shadow(0 2px 10px rgba(10,18,15,.6))",
        }}
      >
        {device === "touch" ? <Main /> : <Souris anime={anime} />}
      </div>
    </div>
  );
}

const trait = {
  fill: "none",
  stroke: CREME,
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

function Main() {
  return (
    <svg width={56} height={64} viewBox="0 0 56 64" {...trait}>
      <circle cx={22} cy={10} r={7} stroke={SAUGE} strokeWidth={1.2} strokeOpacity={0.9} />
      <path d="M22 10 V34 M22 26 C22 22 30 22 30 26 V32 M30 28 C30 24 38 24 38 28 V34 M38 30 C38 27 45 27 45 31 V42 C45 52 39 58 30 58 H27 C21 58 18 55 14 49 L7 38 C5 35 8 31 12 33 L16 36 V34" />
    </svg>
  );
}

function Souris({ anime }: { anime: boolean }) {
  return (
    <svg width={44} height={64} viewBox="0 0 44 64" {...trait}>
      <rect x={4} y={4} width={36} height={56} rx={18} />
      <path d="M22 4 V24 M4 24 H40" />
      <path
        d="M5.5 22.5 V22 A16.5 16.5 0 0 1 20.5 5.6 V22.5 Z"
        fill={SAUGE}
        stroke="none"
        style={{ animation: anime ? `ir-press ${CYCLE}` : "none" }}
      />
    </svg>
  );
}

function Losange() {
  return (
    <span
      aria-hidden
      style={{ width: 6, height: 6, transform: "rotate(45deg)", background: SAUGE, flex: "none" }}
    />
  );
}

/* --- styles (valeurs du handoff) ------------------------------------------ */

const racine: CSSProperties = {
  position: "absolute",
  inset: 0,
  // Sous l'accueil (20), au-dessus du reste de la coquille.
  zIndex: 15,
  overflow: "hidden",
  containerType: "size",
  color: CREME,
  fontFamily: POLICE_UI_SPLASH,
  pointerEvents: "none",
};

/** Sans flou : la scène doit rester nette et lisible derrière. */
const voile: CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "radial-gradient(ellipse 60% 50% at 50% 52%, rgba(16,26,23,.58), rgba(16,26,23,.12) 75%)",
  transition: "opacity .9s ease",
};

const colonne: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 22,
  padding: "0 28px",
  transition: "opacity .8s ease, transform .9s cubic-bezier(.2,.7,.2,1)",
};

const textesStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
  maxWidth: 420,
  textAlign: "center",
};

const surtitre: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: ".16em",
  textTransform: "uppercase",
  color: COULEURS.texteSousTitre,
};

const titre: CSSProperties = {
  margin: 0,
  fontFamily: POLICE_TITRE_SPLASH,
  fontWeight: 400,
  fontSize: "clamp(28px, 3cqw, 40px)",
  lineHeight: 1.15,
  textWrap: "balance",
  textShadow: "0 1px 18px rgba(10,18,15,.6)",
};

const sousTitreStyle: CSSProperties = {
  margin: 0,
  fontSize: 14.5,
  lineHeight: 1.45,
  color: TEXTE_SOUS_TITRE,
  textWrap: "pretty",
  textShadow: "0 1px 12px rgba(10,18,15,.7)",
};

const bouton: CSSProperties = {
  appearance: "none",
  pointerEvents: "auto",
  minHeight: 44,
  padding: "0 22px",
  marginTop: 4,
  color: CREME,
  border: "1px solid rgba(246,238,227,.55)",
  borderRadius: 2,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: ".14em",
  textTransform: "uppercase",
  cursor: "pointer",
};

/** Cible de 44 px de haut : le libellé entier se clique, pas seulement la case. */
const caseLibelle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minHeight: 44,
  marginTop: -10,
  padding: "0 10px",
  fontSize: 12.5,
  color: COULEURS.texteSousTitre,
  textShadow: "0 1px 12px rgba(10,18,15,.7)",
  cursor: "pointer",
};

const caseStyle: CSSProperties = {
  width: 15,
  height: 15,
  margin: 0,
  accentColor: SAUGE,
  cursor: "pointer",
};

const pastilleCadre: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  transition: "opacity .7s ease, transform .7s cubic-bezier(.2,.7,.2,1)",
};

const pastilleStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "11px 18px",
  background: "rgba(16,26,23,.72)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  border: "1px solid rgba(246,238,227,.22)",
  borderRadius: 2,
  fontSize: 13,
  color: CREME,
};
